import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import orderAPI from "../../api/orderApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";
import TableShimmer from "../../components/partials/TableShimmer";
import ShipmentDetail from "./ShipmentDetail";
import SortableRow from "./SortableRow";
import ORDER_CONSTANTS from "../../constants/orderConstants";

function List() {
  const inputRef = useRef();
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [deliveredCount, setDeliveredCount] = useState("0");
  const [processingCount, setProcessingCount] = useState("0");
  const [shippedCount, setShippedCount] = useState("0");
  const [totalRevenue, setTotalRevenue] = useState("0");
  const [tableLoading, setTableLoading] = useState(false);

  const [orderData, setOrderData] = useState([]);

  const [pagination, setPagination] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [newStatus, setNewStatus] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  var { id } = useParams();
  var path = id !== "all" ? id : "";

  const query = useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      status: searchParams.get("status") || path || "",
      search: searchParams.get("search") || "",
      daterange: searchParams.get("daterange") || "",
    }),
    [searchParams, path],
  );

  useEffect(() => {
    fetchDashboardData();
  }, [query]);

  const fetchDashboardData = async () => {
    setTableLoading(true);

    try {
      const res = await orderAPI.list(query);

      if (res.code === 200) {
        const result = res.data;

        setOrderData(result.orders);
        setDeliveredCount(result.deliveredCount);
        setProcessingCount(result.processingCount);
        setShippedCount(result.shippedCount);
        setPagination(result.pagination);
        setTotalRevenue(result.totalrevenue);
      }
    } catch (err) {
    } finally {
      setTableLoading(false);
    }
  };

  const updateFilter = useCallback(
    (key, value) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (value) params.set(key, value);
        else params.delete(key);
        return params;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!inputRef.current) return;

    const $input = window.$(inputRef.current);
    const start = moment().subtract(6, "days");
    const end = moment();

    function booking_range(start, end) {
      const formatted = `${start.format("YYYY-MM-DD")} to ${end.format(
        "YYYY-MM-DD",
      )}`;
      $input.val(formatted);
      updateFilter("daterange", formatted);
    }

    $input.daterangepicker(
      {
        autoUpdateInput: false,
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      booking_range,
    );

    $input.on("apply.daterangepicker", function (ev, picker) {
      const value = `${picker.startDate.format(
        "YYYY-MM-DD",
      )} to ${picker.endDate.format("YYYY-MM-DD")}`;
      $input.val(value);
      updateFilter("daterange", value);
    });

    $input.on("cancel.daterangepicker", function () {
      $input.val("");
      updateFilter("daterange", "");
    });

    // Set initial value if exists
    const currentValue = searchParams.get("daterange");
    if (currentValue) {
      $input.val(currentValue);
      const [startStr, endStr] = currentValue.split(" to ");
      const start = moment(startStr, "YYYY-MM-DD");
      const end = moment(endStr, "YYYY-MM-DD");
      $input.data("daterangepicker").setStartDate(start);
      $input.data("daterangepicker").setEndDate(end);
    }

    return () => {
      $input.data("daterangepicker")?.remove();
    };
  }, [updateFilter, searchParams]);

  const allIds = useMemo(() => orderData.map((o) => o._id), [orderData]);

  const handleSelectAll = useCallback(() => {
    setSelectedOrders((prev) => (prev.length === allIds.length ? [] : allIds));
  }, [allIds]);

  const handleSelectOne = useCallback(
    (id) => {
      setSelectedOrders((prev) => {
        const exists = prev.includes(id);
        const updated = exists ? prev.filter((i) => i !== id) : [...prev, id];

        setSelectAll(updated.length === orderData.length);
        return updated;
      });
    },
    [orderData.length],
  );

  const updateOrders = useCallback(async () => {
    if (!newStatus || selectedOrders.length === 0) return;

    const res = await orderAPI.updateStatus({
      orders: selectedOrders,
      status: newStatus,
    });

    if (res.code === 200) {
      fetchDashboardData();
      showSuccess(res.message);
      setSelectedOrders([]);
      setSelectAll(false);
    } else {
      showError(res.message);
    }
  }, [newStatus, selectedOrders]);

  const pageTitle = useMemo(() => {
    let title = "Order List";

    if (query.status) {
      title += ` for ${query.status.toUpperCase()} Orders`;
    }
    if (query.daterange) title += ` between ${query.daterange}`;
    if (query.search) title += ` for "${query.search}"`;

    return title;
  }, [query]);

  // =========================================
  // Export Orders Function
  // =========================================
  const handleExportOrders = async (fileFormat = "excel") => {
    try {
      setExportLoading(true);

      const exportParams = {
        ...query,
        format: fileFormat,
      };

      // Interceptor ab direct Blob object return karega
      const blobData = await orderAPI.exportOrders(exportParams);

      const fileExtension = fileFormat === "pdf" ? "pdf" : "xlsx";
      const fileName = `Orders_Report_${moment().format("YYYYMMDD_HHmmss")}.${fileExtension}`;

      // Download File
      const url = window.URL.createObjectURL(blobData);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(
        `Orders exported successfully as ${fileFormat.toUpperCase()}`,
      );
    } catch (error) {
      console.error("Export Error:", error);
      showError("Failed to export orders. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>
                {path ? path.charAt(0).toUpperCase() + path.slice(1) : "All"}{" "}
                Orders{" "}
              </h4>
              <h6>Keep track and manage your orders efficiently</h6>
            </div>
          </div>
        </div>

        <div className="welcome align-items-center justify-content-between">
          <div className="row">
            <div className="col-md-6 d-flex align-items-center welcome-text">
              <h3 className="align-items-center">
                <img
                  src={`${process.env.PUBLIC_URL}/img/icons/hi.svg`}
                  alt="img"
                />
                &nbsp;Hi,
              </h3>
              &nbsp;<h6>here's what's happening with your store today.</h6>
            </div>
            <div className="col-md-6 d-flex">
              <div className="me-2 flex-grow-1" style={{ width: "220px" }}>
                <div className="input-icon-start position-relative">
                  <span className="input-icon-addon fs-16 text-gray-9">
                    <i className="ti ti-calendar" />
                  </span>
                  <input
                    type="text"
                    ref={inputRef}
                    className="form-control date-range bookingrange"
                    placeholder="Search by Date"
                    defaultValue={searchParams.get("daterange") || ""}
                  />
                </div>
              </div>

              <div className="me-2" style={{ width: "160px" }}>
                <select
                  className="form-control"
                  value={searchParams.get("status") || path || ""}
                  onChange={(e) => updateFilter("status", e.target.value)}
                >
                  <option value="">Select Status</option>
                  {Object.entries(ORDER_CONSTANTS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="me-2" style={{ width: "200px" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Customer Name/OrderId"
                  value={searchParams.get("search") || ""}
                  onChange={(e) => updateFilter("search", e.target.value)}
                />
              </div>

              <div className="dropdown ms-2">
                <button
                  className="btn btn-primary dropdown-toggle d-flex align-items-center gap-1"
                  type="button"
                  id="exportDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-download fs-16 me-1" />
                      Export
                    </>
                  )}
                </button>

                <ul
                  className="dropdown-menu dropdown-menu-end"
                  aria-labelledby="exportDropdown"
                >
                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => handleExportOrders("excel")}
                    >
                      <i className="ti ti-file-spreadsheet text-success fs-18" />
                      Export as Excel (.xlsx)
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => handleExportOrders("pdf")}
                    >
                      <i className="ti ti-file-text text-danger fs-18" />
                      Export as PDF (.pdf)
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="row sales-cards">
          <div className="col-xl-3 col-sm-12 col-12 d-flex">
            <div className="card d-flex align-items-center justify-content-between flex-fill mb-4">
              <div>
                <h6>Total Earning</h6>
                <h3>
                  ₹
                  <span className="counters">
                    {totalRevenue.toLocaleString("en-IN")}
                  </span>
                </h3>
              </div>
              <img
                src={`${process.env.PUBLIC_URL}/img/icons/weekly-earning.svg`}
                alt="img"
              />
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card color-info bg-primary flex-fill mb-4">
              <div className="mb-2">
                <i className="ti ti-repeat fs-24" />
              </div>
              <h3 className="counters">{processingCount ?? ""}</h3>
              <p>New Orders</p>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card color-info bg-secondary flex-fill mb-4">
              <div className="mb-2">
                <i className="ti ti-gift fs-24" />
              </div>
              <h3 className="counters">{deliveredCount ?? ""}</h3>
              <p>Delivered Orders</p>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card color-info bg-secondary flex-fill mb-4">
              <div className="mb-2">
                <i className="ti ti-brand-pocket fs-24" />
              </div>
              <h3 className="counters">{shippedCount ?? ""}</h3>
              <p>Shipped Orders</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4>{pageTitle}</h4>

            {selectedOrders.length > 0 && (
              <div className="d-flex gap-2 align-items-center">
                <select
                  className="form-select"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  style={{ width: "200px" }}
                >
                  <option value="">-- Choose Status --</option>

                  {Object.entries(ORDER_CONSTANTS).map(([key, label]) => (
                    <option value={key}>Mark as {label}</option>
                  ))}
                </select>

                <button
                  className="btn btn-primary"
                  disabled={!selectedOrders.length || !newStatus}
                  onClick={updateOrders}
                >
                  Update Selected Orders ({selectedOrders.length})
                </button>
              </div>
            )}
          </div>
          <div className="card-body p-0">
            <div className="row">
              <div className="col-md-12">
                <div className="countablepagination p-2">
                  <p>
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total,
                    )}{" "}
                    of {pagination.total} results
                  </p>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th className="no-sort">
                      <label className="checkboxs" htmlFor="select-all">
                        <input
                          type="checkbox"
                          id="select-all"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                        <span className="checkmarks" />
                      </label>
                    </th>
                    <th>Customer Details</th>
                    <th>Order Details </th>
                    <th>Order Status</th>
                    <th>Payment Status</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={10} cols={7} />
                  ) : orderData.length > 0 ? (
                    orderData.map((order) => (
                      <SortableRow
                        key={order._id}
                        order={order}
                        selectedOrders={selectedOrders}
                        handleSelectOne={handleSelectOne}
                        setSelectedOrder={setSelectedOrder}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted p-4">
                        No orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        style={{ width: "750px" }}
        id="offcanvasShipment"
        aria-labelledby="offcanvasShipmentLabel"
      >
        {selectedOrder ? (
          <ShipmentDetail item={selectedOrder} />
        ) : (
          <p className="p-3">Loading...</p>
        )}
      </div>
    </>
  );
}

export default List;
